import React, { useEffect, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AutosizeTextarea } from "@/components/ui/autosize-textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Pencil,
  Trash2,
  Check,
  X,
  Bot,
  User,
  ExternalLink,
  Database,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { StandardAgentThreadView } from "@/modules/standardAgent/components/StandardAgentThreadView";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import { AssetDisplay } from "@/components/AssetDisplay";
import { NoteAssetUpload } from "./NoteAssetUpload";
import { useERPProviderDisplayName } from "./useERPProviderDisplayName";
import { getUserDisplayName, useOrgMembers } from "@/store/teamsStore";
import type { Note, OutputAsset } from "@forgepoint/shared";

interface NoteItemProps {
  note: Note;
  onDelete: (noteId: string) => void;
  onUpdate: (noteId: string, content: string, assetIds?: string[]) => void;
  isNew?: boolean;
}

/**
 * Renders a single note with avatar, metadata, content, and edit/delete actions.
 * For agent-created notes, the agent name is clickable to open the originating thread,
 * followed by the user the agent wrote the note on behalf of.
 * ERP-synced notes are read-only, and notes pulled from an ERP show it as the author.
 * @param note - The note data to display
 * @param onDelete - Callback when the note is deleted
 * @param onUpdate - Callback when the note content is updated
 * @param isNew - Whether this note was just created (triggers highlight animation)
 */
export const NoteItem: React.FC<NoteItemProps> = ({
  note,
  onDelete,
  onUpdate,
  isNew = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);
  const [editAssets, setEditAssets] = useState<OutputAsset[]>(
    note.assets ?? [],
  );
  const [isThreadViewOpen, setIsThreadViewOpen] = useState(false);
  const [showHighlight, setShowHighlight] = useState(isNew);
  const cardRef = useRef<HTMLDivElement>(null);

  const standardAgentName = useStandardAgentDisplayName();
  const usersById = useOrgMembers();
  const isAgent = note.actorType === "AGENT_WORKFLOW_RUN";
  const isErpSync = note.actorType === "ERP_SYNC";
  const isSynced = note.externalMappings.length > 0;
  const erpProviderName = useERPProviderDisplayName(
    isErpSync ? note.actorId : null,
  );
  const displayName =
    erpProviderName ?? (isAgent ? standardAgentName : note.actorName || "User");
  const hasThread = isAgent && Boolean(note.actorId);
  const authorPictureUrl =
    !isAgent && !isErpSync && note.actorId
      ? (usersById.get(note.actorId)?.profilePictureUrl ?? null)
      : null;
  const behalfMember = note.userId ? usersById.get(note.userId) : undefined;

  useEffect(() => {
    if (!isNew) {
      return;
    }
    const timeout = setTimeout(() => setShowHighlight(false), 1500);
    return () => clearTimeout(timeout);
  }, [isNew]);

  /**
   * Extracts up to two initials from a display name.
   * @param name - The name to extract initials from
   * @returns Uppercase initials string
   */
  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  /**
   * Submits the edited content and attachments, then exits edit mode.
   */
  const handleSaveEdit = () => {
    const trimmed = editContent.trim();
    const originalAssetIds = (note.assets ?? []).map((a) => a.id);
    const editAssetIds = editAssets.map((a) => a.id);
    const assetsChanged =
      originalAssetIds.length !== editAssetIds.length ||
      originalAssetIds.some((id, i) => id !== editAssetIds[i]);

    if (!trimmed || (trimmed === note.content && !assetsChanged)) {
      setIsEditing(false);
      setEditContent(note.content);
      setEditAssets(note.assets ?? []);
      return;
    }

    onUpdate(note.id, trimmed, assetsChanged ? editAssetIds : undefined);
    setIsEditing(false);
  };

  /**
   * Cancels editing and restores the original content and attachments.
   */
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(note.content);
    setEditAssets(note.assets ?? []);
  };

  /**
   * Handles keyboard shortcuts in the edit textarea.
   * @param e - The keyboard event
   */
  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSaveEdit();
    }
    if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  return (
    <div
      ref={cardRef}
      className={`
        flex gap-3 p-3 rounded-lg group
        border-l-2 border border-b border-r border-t
        transition-all duration-300 ease-out
        hover:shadow-sm
        ${isAgent ? "border-l-primary/40 hover:border-l-primary/60" : "border-l-transparent hover:border-l-border"}
        ${showHighlight ? "bg-primary/5 border-primary/20" : "hover:bg-muted/30"}
      `}
    >
      <Avatar
        className={`h-8 w-8 shrink-0 ${isAgent ? "bg-primary/10 text-primary" : ""}`}
      >
        {authorPictureUrl && (
          <AvatarImage src={authorPictureUrl} alt={displayName} />
        )}
        <AvatarFallback
          className={`text-xs ${isAgent ? "bg-primary/10 text-primary" : ""}`}
        >
          {isErpSync && <Database className="h-4 w-4" />}
          {isAgent && <Bot className="h-4 w-4" />}
          {!isAgent && !isErpSync && getInitials(displayName)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
          {hasThread ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm font-medium underline decoration-primary/30 underline-offset-2 hover:text-primary hover:decoration-primary transition-colors"
                  onClick={() => setIsThreadViewOpen(true)}
                >
                  {displayName}
                  <ExternalLink className="h-3 w-3 shrink-0 opacity-50" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                View agent thread
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-sm font-medium">{displayName}</span>
          )}
          {behalfMember && (
            <span className="text-sm">
              <span className="text-muted-foreground">on behalf of</span>{" "}
              <span className="font-medium">
                {getUserDisplayName(behalfMember)}
              </span>
            </span>
          )}
          {!isErpSync && (
            <Badge
              variant={isAgent ? "outline" : "secondary"}
              className={`text-[10px] shrink-0 px-1.5 py-0 h-5 ${isAgent ? "border-primary/30 text-primary" : ""}`}
            >
              {isAgent ? (
                <>
                  <Bot className="h-2.5 w-2.5 mr-0.5" />
                  Agent
                </>
              ) : (
                <>
                  <User className="h-2.5 w-2.5 mr-0.5" />
                  User
                </>
              )}
            </Badge>
          )}
          {isSynced && (
            <Badge
              variant="outline"
              className="text-[10px] font-normal tracking-wider uppercase shrink-0 px-1.5 py-0 h-5 border-muted-foreground/25"
            >
              Synced
            </Badge>
          )}
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="text-xs text-muted-foreground shrink-0 cursor-default">
                {formatDistanceToNow(new Date(note.createdAt), {
                  addSuffix: true,
                })}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {new Date(note.createdAt).toLocaleString()}
            </TooltipContent>
          </Tooltip>
        </div>

        {isEditing ? (
          <div className="space-y-2 animate-in fade-in duration-150">
            <AutosizeTextarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              onKeyDown={handleEditKeyDown}
              minHeight={60}
              maxHeight={200}
              className="text-sm focus-visible:ring-1 focus-visible:ring-ring/30"
              autoFocus
            />
            <NoteAssetUpload
              attachedAssets={editAssets}
              onAssetAdded={(asset) =>
                setEditAssets((prev) => [...prev, asset])
              }
              onAssetRemoved={(assetId) =>
                setEditAssets((prev) => prev.filter((a) => a.id !== assetId))
              }
            />
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="default"
                className="h-7 text-xs"
                onClick={handleSaveEdit}
              >
                <Check className="h-3 w-3 mr-1" />
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={handleCancelEdit}
              >
                <X className="h-3 w-3 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm break-words whitespace-pre-wrap leading-relaxed">
              {note.content}
            </p>
            {note.assets && note.assets.length > 0 && (
              <AssetDisplay assets={note.assets} />
            )}
          </>
        )}
      </div>

      {!isEditing && !isSynced && (
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0 self-center">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 rounded-md hover:bg-muted"
                onClick={() => {
                  setEditContent(note.content);
                  setEditAssets(note.assets ?? []);
                  setIsEditing(true);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Edit
            </TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                onClick={() => onDelete(note.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Delete
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      {hasThread && (
        <StandardAgentThreadView
          threadId={note.actorId}
          isOpen={isThreadViewOpen}
          onClose={() => setIsThreadViewOpen(false)}
        />
      )}
    </div>
  );
};
